import { Link } from 'react-router-dom';

export default function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-maritime-950 text-slate-300 border-t border-maritime-800/80 relative overflow-hidden">
      {/* Decorative subtle maritime depth lines */}
      <div className="absolute inset-0 opacity-5 pointer-events-none" aria-hidden="true">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="nautical-grid" width="60" height="60" patternUnits="userSpaceOnUse">
              <path d="M 60 0 L 0 0 0 60" fill="none" stroke="currentColor" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#nautical-grid)" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-12">
          {/* Col 1: Brand & Slogan */}
          <div className="space-y-4">
            <Link to="/" className="inline-block focus:outline-none focus:ring-2 focus:ring-cyan-400 rounded">
              <img
                src="https://assets.auxodev.dk/assets/a8b5f246-9eaa-4cb1-adf9-4b882abefbbd/logo.png"
                alt="JT transportsejlads"
                className="h-16 sm:h-20 w-auto object-contain brightness-0 invert"
              />
            </Link>
            <p className="text-sm text-cyan-400 font-semibold tracking-wide uppercase">
              Din bådtransportør på vandet
            </p>
            <p className="text-sm text-slate-400 leading-relaxed">
              Professionel og sikker bådtransport på vandet i Mårslet, Aarhus, Horsens og hele Østjylland. Med mange års maritim erfaring og dueligheds- samt skipperuddannelse.
            </p>
            {/* Nautical coordinates accent */}
            <div className="pt-2 text-xs font-mono text-slate-500 flex items-center gap-2">
              <i className="fa-solid fa-compass text-cyan-500" aria-hidden="true" />
              <span>56°03'N · 10°09'Ø (Mårslet)</span>
            </div>
          </div>

          {/* Col 2: Navigation Links */}
          <div className="space-y-3">
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider">
              Sider
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/" className="hover:text-cyan-400 transition-colors">
                  Forside
                </Link>
              </li>
              <li>
                <Link to="/baadtransport" className="hover:text-cyan-400 transition-colors">
                  Bådtransport
                </Link>
              </li>
              <li>
                <Link to="/referencer" className="hover:text-cyan-400 transition-colors">
                  Referencer (Seneste arbejde)
                </Link>
              </li>
              <li>
                <Link to="/reference" className="hover:text-cyan-400 transition-colors">
                  Reference (Skræddersyede løsninger)
                </Link>
              </li>
              <li>
                <Link to="/om-mig" className="hover:text-cyan-400 transition-colors">
                  Om mig
                </Link>
              </li>
              <li>
                <Link to="/kontakt" className="hover:text-cyan-400 transition-colors">
                  Kontakt
                </Link>
              </li>
            </ul>
          </div>

          {/* Col 3: Virksomhedsoplysninger */}
          <div className="space-y-3">
            <h3 className="text-white font-semibold text-sm uppercase tracking-wider">
              Kontaktoplysninger
            </h3>
            <div className="space-y-2.5 text-sm">
              <p className="font-semibold text-white">JT transportsejlads</p>
              <p>
                <a
                  href="https://www.google.com/maps/place/Moesg%C3%A5rd%20All%C3%A9%205,%208320%20M%C3%A5rslet"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-cyan-400 transition-colors flex items-start gap-2"
                >
                  <i className="fa-solid fa-location-dot text-cyan-400 mt-1 flex-shrink-0" aria-hidden="true" />
                  <span>Moesgård Allé 5, 8320 Mårslet</span>
                </a>
              </p>
              <p>
                <a
                  href="tel:+4550510062"
                  className="hover:text-cyan-400 transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-cyan-400" aria-hidden="true" />
                  <span className="whitespace-nowrap font-medium">+45 50 51 00 62</span>
                </a>
              </p>
              <p>
                <a
                  href="mailto:johann2@hotmail.com"
                  className="hover:text-cyan-400 transition-colors flex items-center gap-2 whitespace-nowrap"
                >
                  <i className="fa-solid fa-envelope text-cyan-400" aria-hidden="true" />
                  <span className="whitespace-nowrap">johann2@hotmail.com</span>
                </a>
              </p>
              <p className="text-xs text-slate-400 pt-1">
                CVR nr.: 45455335
              </p>
              <div className="pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-semibold uppercase tracking-wider transition-colors shadow-md"
                >
                  <span>Kontakt mig i dag</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-8 border-t border-maritime-800/80 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-400">
          <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 text-center sm:text-left">
            <p>© {new Date().getFullYear()} JT transportsejlads. Alle rettigheder forbeholdes.</p>
            <span className="hidden sm:inline text-slate-600">•</span>
            <div className="flex items-center gap-3">
              <a
                href="https://www.iubenda.com/privacy-policy/30907272"
                className="iubenda-white iubenda-noiframe iubenda-embed hover:text-white transition-colors"
                title="Privatlivspolitik "
              >
                Privatlivspolitik
              </a>
              <span className="text-slate-600">•</span>
              <a
                href="https://www.iubenda.com/privacy-policy/30907272/cookie-policy"
                className="iubenda-white iubenda-noiframe iubenda-embed hover:text-white transition-colors"
                title="Cookiepolitik "
              >
                Cookiepolitik
              </a>
            </div>
          </div>

          <button
            type="button"
            onClick={scrollToTop}
            className="flex items-center gap-2 text-slate-300 hover:text-cyan-400 transition-colors focus:outline-none focus:underline"
            aria-label="Gå til toppen af siden"
          >
            <span>Gå til toppen</span>
            <i className="fa-solid fa-arrow-up text-cyan-400" aria-hidden="true" />
          </button>
        </div>
      </div>
    </footer>
  );
}
